import { useCallback, useEffect, useRef, useState } from "react";
import { Package, Upload, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";

const Catalog = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [brands, setBrands] = useState([]);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [discontinuedCount, setDiscontinuedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/catalog", { params: { search: search || undefined, brand: brand || undefined, page, limit: 50, include_discontinued: showDiscontinued || undefined } });
      setItems(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, brand, page, showDiscontinued]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const loadSummary = useCallback(() => {
    api.get("/catalog/summary").then((r) => {
      setBrands(r.data.brands);
      setDiscontinuedCount(r.data.discontinued_articles || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/catalog/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} new, updated ${r.data.updated} articles from ${r.data.file}`);
      setPage(1);
      load();
      loadSummary();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="catalog-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Product Catalog</h1>
          <p className="text-base text-slate-600 dark:text-slate-400">
            Busy export import — Article No → Sizes → Qty. <span className="font-mono text-cyan-600 dark:text-cyan-400">{total}</span> articles.
          </p>
        </div>
        <label
          data-testid="busy-excel-import-btn"
          className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors"
        >
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {importing ? "Importing…" : "Import Busy Excel"}
          <input
            ref={fileRef}
            data-testid="busy-excel-file-input"
            type="file"
            accept=".xls,.xlsx,.csv"
            className="hidden"
            onChange={(e) => importFile(e.target.files?.[0])}
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600 dark:text-slate-400" />
          <input
            data-testid="catalog-search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search article no or name…"
            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 pl-9 pr-3 py-2 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
          />
        </div>
        <select
          data-testid="catalog-brand-filter"
          value={brand}
          onChange={(e) => {
            setBrand(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base text-slate-700 dark:text-slate-300 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        >
          <option value="">All brands</option>
          {brands.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <button
          data-testid="catalog-discontinued-toggle"
          onClick={() => {
            setShowDiscontinued(!showDiscontinued);
            setPage(1);
          }}
          className={`rounded-md border px-3 py-2 text-base font-semibold transition-colors ${
            showDiscontinued
              ? "border-amber-400 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300"
              : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          {showDiscontinued ? "Hiding old versions" : `Show old versions${discontinuedCount ? ` (${discontinuedCount})` : ""}`}
        </button>
      </div>

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden" data-testid="catalog-table-wrap">
        {loading ? (
          <div className="flex h-40 items-center justify-center" data-testid="catalog-loading">
            <div className="h-8 w-8 rounded-full border-2 border-emerald-500 dark:border-emerald-500/60 border-t-transparent animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="catalog-empty">
            <Package className="mx-auto h-8 w-8 text-slate-700 dark:text-slate-300 mb-2" />
            <p className="text-base text-slate-600 dark:text-slate-400">
              No articles yet. Import a Busy Excel export with columns like Article No, Name, Brand, Size, Qty, Rate, MRP, Discount.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-base" data-testid="catalog-table">
              <thead>
                <tr className="border-b border-slate-300 dark:border-slate-700 text-left text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">
                  <th className="px-4 py-3">Photo</th>
                  <th className="px-4 py-3">Article No</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Brand</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">MRP</th>
                  <th className="px-4 py-3 text-right">Disc%</th>
                  <th className="px-4 py-3 text-right">Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={`${it.article_no}-${it.size}-${it.brand}`} data-testid={`catalog-row-${it.article_no}`} className={`border-b border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${it.status === "discontinued" ? "opacity-60" : ""}`}>
                    <td className="px-4 py-2.5">
                      {it.image_path ? (
                        <a href={fileUrl(it.image_path)} target="_blank" rel="noopener noreferrer" data-testid={`catalog-photo-link-${it.article_no}`}>
                          <img
                            src={fileUrl(it.image_path)}
                            alt={it.name || it.article_no}
                            data-testid={`catalog-photo-${it.article_no}`}
                            className="h-12 w-12 rounded-md object-cover border border-slate-300 dark:border-slate-700"
                          />
                        </a>
                      ) : (
                        <span className="text-slate-600 dark:text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-cyan-600 dark:text-cyan-400">
                      {it.article_no}
                      {it.status === "discontinued" && (
                        <span data-testid={`catalog-discontinued-badge-${it.article_no}`} className="ml-2 rounded bg-amber-100 dark:bg-amber-500/20 px-1.5 py-0.5 text-xs font-bold font-mono uppercase tracking-wider text-amber-700 dark:text-amber-300">Old</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{it.name || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{it.brand}</td>
                    <td className="px-4 py-2.5 font-mono text-slate-600 dark:text-slate-400">{it.size || "—"}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${it.qty > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>{it.qty}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">{it.rate != null ? `₹${it.rate}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">{it.mrp != null ? `₹${it.mrp}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-amber-600 dark:text-amber-400">{it.discount_pct != null ? `${it.discount_pct}%` : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap" data-testid={`catalog-updated-${it.article_no}`}>
                      {it.updated_at ? formatDistanceToNow(new Date(it.updated_at), { addSuffix: true }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="catalog-pagination">
          <button
            data-testid="catalog-prev-page"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="rounded-md border border-slate-300 dark:border-slate-700 p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-mono text-slate-600 dark:text-slate-400">page {page} / {pages}</span>
          <button
            data-testid="catalog-next-page"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
            className="rounded-md border border-slate-300 dark:border-slate-700 p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Catalog;
